import { apiBaseUrl } from "api/api";
import { POLICY_VERSION } from "constraints/consent";

/**
 * Cookie consent state, storage, and the anonymous session identifier.
 *
 * Design constraints that drove this, all from the spec:
 *
 *  - **Nothing non-essential may fire before a decision.** So consent state is
 *    read synchronously from a first-party cookie at module load, before any
 *    third-party script is injected. No await, no network round-trip on the
 *    critical path.
 *  - **The routed-participant flow must not be delayed.** Persisting to the
 *    backend is fire-and-forget; the local decision applies immediately and the
 *    survey handoff never waits on it.
 *  - **Strictly necessary is not a category.** It has no toggle and no stored
 *    value, because it is not a choice.
 */

export const COOKIE_CATEGORIES = ["analytics", "functional", "marketing"] as const;
export type CookieCategory = (typeof COOKIE_CATEGORIES)[number];

/** Maps a UI category to the backend consent purpose. */
export const CATEGORY_TO_PURPOSE: Record<CookieCategory, string> = {
  analytics: "COOKIE_ANALYTICS",
  functional: "COOKIE_FUNCTIONAL",
  marketing: "COOKIE_MARKETING",
};

export type CookieConsentState = {
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  /** ISO timestamp of the decision. */
  decidedAt: string;
  /** Policy version the decision was made against. */
  version: string;
};

/** Strictly-necessary only. Used before any decision exists. */
export const NO_CONSENT: CookieConsentState = {
  analytics: false,
  functional: false,
  marketing: false,
  decidedAt: "",
  version: "",
};

const CONSENT_COOKIE = "zs_cookie_consent";
const SESSION_COOKIE = "zs_anon_id";
/** Long enough not to re-ask constantly; short enough that consent is refreshed. */
const CONSENT_MAX_AGE_DAYS = 180;
const SESSION_MAX_AGE_DAYS = 365;

const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name: string, value: string, days: number) => {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // SameSite=Lax so the cookie survives the vendor redirect into a survey.
  // Strict would drop it on cross-site navigation and re-show the banner
  // mid-flow. Not HttpOnly by necessity: the gating decision is made in JS.
  document.cookie = `${name}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax${
    window.location.protocol === "https:" ? "; Secure" : ""
  }`;
};

const deleteCookie = (name: string) => {
  if (typeof document === "undefined") return;
  // Cleared on the current path and on the bare domain, and again without a
  // leading dot, because third-party tags are inconsistent about which they set.
  const host = window.location.hostname;
  const domains = [undefined, host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  for (const domain of domains) {
    document.cookie = `${name}=; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Path=/${
      domain ? `; Domain=${domain}` : ""
    }`;
  }
};

/**
 * An unguessable anonymous identifier, minted lazily.
 *
 * §9: must be unguessable and must not leak into URLs or third-party requests.
 * So: 128 bits from the platform CSPRNG, base64url, held in a first-party cookie
 * and sent only in the body of our own consent call. Never appended to a query
 * string, and deliberately not derived from anything about the visitor.
 */
export const getAnonymousSessionId = (): string => {
  const existing = readCookie(SESSION_COOKIE);
  if (existing && /^[A-Za-z0-9_-]{22,64}$/.test(existing)) {
    return existing;
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    // Only reachable on a browser without Web Crypto. Weaker, and the id is not
    // a credential -- it keys a cookie preference, nothing more.
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }

  // Array.from rather than spread: tsconfig targets es5, where iterating a
  // typed array needs downlevelIteration.
  const id = btoa(String.fromCharCode.apply(null, Array.from(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

  writeCookie(SESSION_COOKIE, id, SESSION_MAX_AGE_DAYS);
  return id;
};

/**
 * Reads the stored decision, or null if none exists.
 *
 * Synchronous and dependency-free so it can be consulted before the first script
 * injection. A decision made against an older policy version is treated as
 * absent, so a policy change re-asks rather than silently inheriting old consent.
 */
export const readStoredConsent = (): CookieConsentState | null => {
  const raw = readCookie(CONSENT_COOKIE);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<CookieConsentState>;
    if (parsed.version !== POLICY_VERSION) return null;

    return {
      analytics: parsed.analytics === true,
      functional: parsed.functional === true,
      marketing: parsed.marketing === true,
      decidedAt: typeof parsed.decidedAt === "string" ? parsed.decidedAt : "",
      version: POLICY_VERSION,
    };
  } catch {
    return null;
  }
};

export const storeConsent = (state: CookieConsentState) => {
  writeCookie(CONSENT_COOKIE, JSON.stringify(state), CONSENT_MAX_AGE_DAYS);
};

/**
 * Cookies each category is responsible for, removed when it is turned off.
 *
 * §2: moving a category from granted to denied must actually remove the cookies
 * already set in it. Clearing them is best-effort -- a cookie set on a
 * third-party domain cannot be deleted from here, which is precisely why the
 * scripts are never loaded before consent in the first place.
 */
const CATEGORY_COOKIES: Record<CookieCategory, string[]> = {
  analytics: ["_ga", "_gid", "_gat", "_gcl_au", "_gac_", "__utma", "__utmz"],
  functional: ["zslang"],
  marketing: ["_fbp", "_fbc", "fr", "_gcl_aw", "_gcl_dc"],
};

export const clearCategoryCookies = (category: CookieCategory) => {
  if (typeof document === "undefined") return;

  const prefixes = CATEGORY_COOKIES[category];
  const present = document.cookie.split(";").map((c) => c.split("=")[0].trim());

  for (const name of present) {
    if (prefixes.some((p) => name === p || name.startsWith(p))) {
      deleteCookie(name);
    }
  }
};

/**
 * Persists the decision to the backend. FIRE AND FORGET.
 *
 * Never awaited by the caller: the local decision has already been applied, and
 * the routed-participant flow must not gain a network round-trip. A failure here
 * loses the server-side audit row, not the user's choice -- the cookie holds it
 * and the next page load retries.
 */
export const persistConsent = (state: CookieConsentState, anonymousSessionId: string) => {
  const decision = {
    COOKIE_ANALYTICS: state.analytics,
    COOKIE_FUNCTIONAL: state.functional,
    COOKIE_MARKETING: state.marketing,
  };

  try {
    const body = JSON.stringify({ decision, anonymousSessionId });

    // keepalive so the request survives the page navigating away -- which is
    // exactly what happens when a routed participant accepts and moves on.
    void fetch(`${apiBaseUrl}/api/cookie-consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Never let persistence break the page.
  }
};

export const grantAll = (): CookieConsentState => ({
  analytics: true,
  functional: true,
  marketing: true,
  decidedAt: new Date().toISOString(),
  version: POLICY_VERSION,
});

export const rejectNonEssential = (): CookieConsentState => ({
  analytics: false,
  functional: false,
  marketing: false,
  decidedAt: new Date().toISOString(),
  version: POLICY_VERSION,
});

export const customConsent = (choices: Record<CookieCategory, boolean>): CookieConsentState => ({
  analytics: choices.analytics === true,
  functional: choices.functional === true,
  marketing: choices.marketing === true,
  decidedAt: new Date().toISOString(),
  version: POLICY_VERSION,
});
